import { Form, Head, Link, usePage } from "@inertiajs/react"
import { useState } from "react"

import HeadingSmall from "@/components/heading-small"
import InputError from "@/components/input-error"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import AppLayout from "@/layouts/app-layout"
import { teamApiKeyPath, teamApiKeysPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

const breadcrumbs: BreadcrumbItem[] = [
  { title: "API Keys", href: teamApiKeysPath() },
]

interface ApiKey {
  id: number
  name: string
  last_four: string
  last_used_at: string | null
  created_at: string
  creator: { id: number; name: string }
}

interface Props {
  api_keys: ApiKey[]
}

export default function ApiKeysIndex({ api_keys }: Props) {
  const { url, flash } = usePage()
  const plaintext_token = flash.plaintext_token
  const [copied, setCopied] = useState(false)
  const [dismissed, setDismissed] = useState(false)

  function copyToken() {
    if (!plaintext_token) return
    void navigator.clipboard.writeText(plaintext_token).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="API Keys" />

      <div className="space-y-6 px-4 py-6">
        <HeadingSmall
          title="API Keys"
          description={
            <>
              Create API keys to authenticate with the Broadside API. Tokens are
              shown only once.{" "}
              <a
                href="/api_docs"
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-4"
              >
                View API docs
              </a>
              .
            </>
          }
        />

        {plaintext_token && !dismissed && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-700 dark:bg-amber-950/40">
            <p className="text-sm font-medium">
              Your new API key &mdash; copy it now. It will not be shown again.
            </p>
            <div className="mt-3 flex items-center gap-2">
              <code className="bg-background flex-1 overflow-x-auto rounded border px-3 py-2 font-mono text-sm">
                {plaintext_token}
              </code>
              <Button type="button" size="sm" onClick={copyToken}>
                {copied ? "Copied" : "Copy"}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setDismissed(true)}
              >
                Dismiss
              </Button>
            </div>
          </div>
        )}

        <Form
          method="post"
          action={teamApiKeysPath()}
          resetOnSuccess
          options={{ preserveScroll: true }}
          className="max-w-sm space-y-4"
          key={url}
        >
          {({ errors, processing }) => (
            <>
              <div className="grid gap-2">
                <Label htmlFor="api_key_name">Name</Label>
                <Input
                  id="api_key_name"
                  name="api_key[name]"
                  placeholder="e.g. Production publisher"
                  required
                />
                <InputError messages={errors.name} />
              </div>
              <Button disabled={processing}>Create API key</Button>
            </>
          )}
        </Form>

        {api_keys.length === 0 ? (
          <p className="text-muted-foreground text-sm">No API keys yet.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Token</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Last used</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {api_keys.map((apiKey) => (
                <TableRow key={apiKey.id}>
                  <TableCell className="font-medium">{apiKey.name}</TableCell>
                  <TableCell>
                    <code className="text-muted-foreground font-mono text-xs">
                      bs_…{apiKey.last_four}
                    </code>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {new Date(apiKey.created_at).toLocaleString()} by{" "}
                    {apiKey.creator.name}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {apiKey.last_used_at
                      ? new Date(apiKey.last_used_at).toLocaleString()
                      : "Never"}
                  </TableCell>
                  <TableCell className="text-right">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="destructive" size="sm">
                          Revoke
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>
                            Revoke &ldquo;{apiKey.name}&rdquo;?
                          </AlertDialogTitle>
                          <AlertDialogDescription>
                            Any clients using this key will immediately stop
                            working. This cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction asChild variant="destructive">
                            <Link
                              method="delete"
                              href={teamApiKeyPath({ id: apiKey.id })}
                              as="button"
                              preserveScroll
                            >
                              Revoke
                            </Link>
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </AppLayout>
  )
}
